import {Modal, StyleSheet, Text, View} from "react-native";
import {Color} from "@/lib/styles";
import Page from "@/lib/components/Page";
import Center from "@/lib/components/Center";
import {useSession} from "@/lib/auth/auth";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {AccountEditRequest, MyAccount} from "@/lib/types";
import {dataRetryPolicy, fetchBackend, fetchBackendNoContent} from "@/lib/backend";
import ErrorPage from "@/lib/components/ErrorPage";
import SpinnerPage from "@/lib/components/SpinnerPage";
import Nav from "@/lib/components/Nav";
import {useMemo, useState} from "react";
import ErrorMessage from "@/lib/components/ErrorMessage";
import Editable from "@/lib/components/Editable";
import Colorpicker from "@/lib/components/Colorpicker";
import Split from "@/lib/components/Split";
import Avatar from "@/lib/components/Avatar";
import Icon from "@/lib/components/Icon";
import {useRouter} from "expo-router";
import {colorName} from "@/lib/color";
import ActionButton from "@/lib/components/ActionButton";
import Header from "@/lib/components/Header";

type EditableComponent = null | "color";

export default function Index() {

    const {session} = useSession();
    const router = useRouter();
    const queryClient = useQueryClient();

    const [isEditing, setIsEditing] = useState<EditableComponent>(null);
    const [saveError, setSaveError] = useState<Error | null>(null);
    const [busy, setBusy] = useState(false);

    const {data, isPending, error} = useQuery<MyAccount>({
        queryKey: ['account', session],
        queryFn: () => fetchBackend(`/account`, "GET", session),
        retry: dataRetryPolicy,
    })

    const colorNameMemo = useMemo(() => {
        return colorName(data?.avatar.color!);
    }, [data?.avatar.color])

    if (error) {
        return <ErrorPage error={error}/>
    } else if (isPending) {
        return <SpinnerPage/>
    }

    if (busy) return <SpinnerPage/>;

    const saveProfile = async (changes: AccountEditRequest) => {
        setSaveError(null);
        setBusy(true);
        fetchBackendNoContent(`/account`, "PATCH", session, changes).then(async () => {
            await queryClient.invalidateQueries({queryKey: ['account', session]});
            setIsEditing(null);
        }).catch((error) => {
            setSaveError(error);
        }).finally(() => {
            setIsEditing(null);
            setBusy(false);
        });
    }

    const avatar = data.avatar;
    const cameraSize = 32;

    return (
        <Page scrollable={true}>
            <View style={[styles.card, {backgroundColor: avatar.color}]}>
                <Split left={
                    <>
                        <Avatar avatar={avatar} size={40}/>
                        <Text selectable={false} style={[styles.username, {color: avatar.contrast_color}]}>{avatar.username}</Text>
                    </>
                } right={
                    <></>
                }/>
                <Editable
                    color={avatar.contrast_color}
                    style={{
                        flexDirection: "column",
                        alignItems: "center",
                        alignContent: "center",
                    }}
                    onPress={() => {
                        router.navigate("/u/account/picture");
                    }}
                >
                    <Avatar avatar={avatar} pressable={false} size={144}/>
                    <View style={{
                        position: "absolute",
                        left: "34%",
                        top: "77%",
                        height: cameraSize,
                        width: cameraSize,
                        borderRadius: cameraSize,
                        backgroundColor: Color.Background,
                    }}>
                        <Icon kind={"camera"} color={Color.PrimaryText} size={cameraSize}/>
                    </View>
                </Editable>

                <Editable color={avatar.contrast_color} onPress={() => setIsEditing("color")}>
                    <Text style={[styles.editPart, {color: avatar.contrast_color}]}>Farge</Text>
                    <Text style={[styles.editValue, {color: avatar.contrast_color}]}>{colorNameMemo}</Text>
                </Editable>

            </View>

            {saveError && <ErrorMessage error={saveError}/>}

            <Nav>
                <ActionButton
                    title={"Ferdig"}
                    icon={"checkmark"}
                    onPress={async () => {
                        router.dismiss();
                    }}
                />
            </Nav>

            <Modal onRequestClose={() => setIsEditing(null)}
                   visible={!!isEditing}
                   allowSwipeDismissal={true}
                   presentationStyle={"pageSheet"}
                   animationType={"slide"}
            >
                <Page>
                    <Center>
                        <Header title={"Velg profilfarge"} center={true} marginBottom={16}/>
                        <Colorpicker color={avatar.color} onChange={async (color) => {
                            setIsEditing(null);
                            await saveProfile({color: color.color});
                        }}/>
                    </Center>
                </Page>
            </Modal>
        </Page>
    );
}

const styles = StyleSheet.create({
    editPart: {
        fontSize: 16,
        fontWeight: 600,
    },
    editValue: {
        fontSize: 16,
    },
    card: {
        flexDirection: "column",
        borderRadius: 16,
        padding: 6,
        gap: 8,
    },
    username: {
        fontSize: 16,
        fontWeight: 600,
    },
});